import { useEffect, useRef } from 'react';

/**
 * Hook que prende foco dentro de um container (modal/overlay).
 *
 * Atende WCAG 2.1.1 (Keyboard) + 2.4.3 (Focus Order):
 *  - Quando o trap ativa, foca o primeiro elemento focável dentro
 *  - Tab cycle preso dentro do container (Tab no último → volta pro primeiro;
 *    Shift+Tab no primeiro → vai pro último)
 *  - Quando o trap desativa, restaura foco no elemento que tinha foco antes
 *
 * Uso:
 *   const ref = useFocusTrap<HTMLDivElement>(isOpen);
 *   <div ref={ref}>...</div>
 *
 * Limitações:
 *  - Não bloqueia clique no backdrop (intencional — cada modal decide)
 *  - Não detecta elementos focáveis adicionados depois do mount (ex.: render
 *    condicional aninhado). Se for o caso, usar MutationObserver — fora de
 *    escopo aqui.
 *
 * Implementação manual em vez de lib (focus-trap-react): zero dependência,
 * 50 linhas, cobre 95% dos casos de modal.
 */

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useFocusTrap<T extends HTMLElement>(active: boolean) {
  const containerRef = useRef<T | null>(null);
  const lastFocusedBeforeTrapRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Salva o elemento focado ANTES do trap pra restaurar no unmount.
    lastFocusedBeforeTrapRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Foca o primeiro elemento focável dentro do container (ou o próprio
    // container se nenhum filho focável). Atraso de 1 frame pra garantir
    // que o React já pintou os filhos.
    const rafId = requestAnimationFrame(() => {
      const first = container.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
      if (first) {
        first.focus();
      } else if (container.tabIndex >= 0) {
        container.focus();
      }
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
      ).filter((el) => !el.hasAttribute('inert') && el.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        // Shift+Tab no primeiro → vai pro último
        if (activeElement === first || !container.contains(activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab no último → volta pro primeiro
        if (activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKey);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', onKey);
      // Restaura foco no elemento prévio.
      const prev = lastFocusedBeforeTrapRef.current;
      if (prev && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [active]);

  return containerRef;
}
